/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

/**
 * The UE Editor uses the same regex to determine valid href navigateTo values.
 * If this regex is modified consider if the UE editor needs updating as well.
 * Andrea Botti will need to keep in sync.
 */
export const epicOriginRegex =
	/^((www|www2|localhost|rossi|dev|docs|forums|company|legal)(\.staging)?\.)?(unrealengine|epicgames|twinmotion|metahuman|realityscan|kidswebservices|fab|quixel)(\.com|-(ci|gamedev)\.ol\.epicgames\.net)\/?/;

export const isRelative = (href?: string): boolean => {
	return Boolean(href?.startsWith("/")) && !href?.startsWith("//");
};

export const isHash = (href?: string): boolean => {
	return Boolean(href?.startsWith("#"));
};

export const isSameOrigin = (href = ""): boolean => {
	if (!href) return false;

	if (isRelative(href)) {
		return true;
	}

	if (isHash(href)) {
		return true;
	}

	try {
		let cleanedHref = href;
		//prefix if missing protocol
		if (cleanedHref.startsWith("//")) {
			cleanedHref = `https:${cleanedHref}`;
		}
		const url = new URL(cleanedHref);
		const matches = url.hostname.match(epicOriginRegex);
		const sameOrigin = (matches && matches.length && matches[0].length === url.hostname.length) || false;
		return sameOrigin;
	} catch {
		return false;
	}
};

/**
 * Checks if a URL is absolute (starts with http/https or protocol-relative //)
 * @param href - The URL to check
 * @returns boolean - True if URL is absolute
 */
export const isAbsolute = (href?: string): boolean => {
	return Boolean(href?.indexOf("http") === 0) || Boolean(href?.startsWith("//"));
};

/**
 * Checks if a URL is a mailto link
 * @param href - The URL to check
 * @returns boolean - True if URL is a mailto link
 */
export const isMailTo = (href?: string): boolean => {
	return Boolean(href?.indexOf("mailto:") === 0);
};

/**
 * Checks if a URL is an Epic Games Launcher link
 * @param href - The URL to check
 * @returns boolean - True if URL is a launcher link
 */
export const isLauncher = (href?: string): boolean => {
	return Boolean(href?.indexOf("com.epicgames.launcher:") === 0);
};

/**
 * Checks if a pathname contains a locale prefix
 * @param locales - Array of locale codes to check for
 * @param href - The URL/pathname to check
 * @returns boolean - True if pathname has a locale prefix
 */
export const pathnameHasLocale = (locales: string[], href?: string): boolean => {
	if (!href || !locales?.length) return false;

	if (href.startsWith("/api/")) return true;

	return locales.some((locale) => href.startsWith(`/${locale}/`) || href === `/${locale}`);
};

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns boolean - True if URL is valid
 */
export const isValidUrl = (url?: string | URL | null): boolean => {
	if (!url) return false;
	try {
		new URL(url);
		return true;
	} catch {
		console.error("Invalid URL: ", url);
		return false;
	}
};

/**
 * Checks if a URL uses HTTPS protocol
 * @param url - The URL to check
 * @returns boolean - True if URL uses HTTPS
 */
export const isHttpsUrl = (url?: string | URL | null): boolean => {
	if (!url || !isValidUrl(url)) return false;
	if (new URL(url).protocol !== "https:") return false;
	return true;
};

/**
 * Checks if a URL matches the configured server origin
 * @param href - The URL to check
 * @returns boolean - True if URL matches server origin
 */
export const isOriginServerUrl = (href: string): boolean => {
	try {
		// Check if it's an absolute URL
		const url = new URL(href);

		// Get the configured server URL origin
		const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;
		if (serverUrl) {
			const serverOrigin = new URL(serverUrl).origin;
			// If the URL's origin matches the server origin, return true
			if (url.origin === serverOrigin) {
				return true;
			}
		}

		// It's an absolute URL that doesn't match our origin
		return false;
	} catch {
		// If URL parsing fails, not a valid absolute URL
		return false;
	}
};
